/**
 * Persona oluşturma sihirbazı — "başlık ver, kimlik al".
 *
 * İki adımlı olması bilinçli: LLM taslağını doğrudan kaydetmek kütüphaneyi
 * terk edilmiş ilk denemelerle doldurur. Kullanıcı taslağı GÖRÜR, mizacını
 * ayarlar, sonra kaydeder.
 *
 * Adım 1 — başlık (+ opsiyonel yönlendirme, +opsiyonel bilişsel duruş)
 * Adım 2 — taslağı gözden geçir: ad, unvan, özet, görev tanımı, talimat,
 *          Big Five değerleri. Hepsi düzenlenebilir.
 */
import { useState } from "react";
import { useModelsCatalog } from "../../hooks/useModelsCatalog";
import type { AgentRole } from "../../domain/types";
import type {
  LibraryCatalog,
  LibraryPersona,
  PersonaDraft,
  PsychometricProfile,
} from "../../domain/personaLibrary";
import {
  LibraryApiError,
  createPersona,
  synthesizePersona,
} from "../../transport/personaLibraryClient";
import { PsychometricEditor } from "./PsychometricEditor";
import {
  Btn,
  ErrorBanner,
  Field,
  Modal,
  PersonaAvatar,
  Select,
  TextArea,
  TextInput,
} from "./ui";

const ROLE_LABELS: Partial<Record<AgentRole, string>> = {
  "pragmatic-architect": "Pragmatik Mimar",
  "risk-officer": "Risk Sorumlusu",
  innovator: "Yenilikçi",
  "devils-advocate": "Şeytanın Avukatı",
  "ux-advocate": "Kullanıcı Savunucusu",
  "ethics-steward": "Etik Sorumlusu",
  "strategic-visionary": "Stratejik Vizyoner",
  "technical-authority": "Teknik Otorite",
  "threat-analyst": "Tehdit ve Risk Analisti",
  contrarian: "Muhalif Ses",
  "operational-commander": "Operasyonel Yönetici",
  "compliance-officer": "Uyum ve Regülasyon",
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role as AgentRole] ?? role;
}

const EXAMPLES = [
  "Kıdemli Vergi Müfettişi",
  "Savunma Sanayi Tedarik Zinciri Uzmanı",
  "Klinik Araştırma Direktörü",
  "Siber Tehdit İstihbaratı Analisti",
];

export function PersonaComposer({
  open,
  onClose,
  catalog,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  catalog: LibraryCatalog | null;
  onCreated: (persona: LibraryPersona) => void;
}) {
  const [title, setTitle] = useState("");
  const [hint, setHint] = useState("");
  const [role, setRole] = useState<string>("");
  const [draft, setDraft] = useState<PersonaDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Boş = katalog varsayılanı. Kütüphaneye kaydedilen persona bir tartışmaya
  // çağrıldığında bu model kullanılır; seçim yapılmazsa konsey ataması karar
  // verir. Sihirbazda zaten olan bu kontrol kütüphanede yoktu, dolayısıyla
  // kütüphaneden gelen her persona varsayılan modele düşüyordu.
  const [modelId, setModelId] = useState<string>("");
  const modelCatalog = useModelsCatalog();

  function reset() {
    setTitle("");
    setHint("");
    setRole("");
    setDraft(null);
    setModelId("");
    setError(null);
    setBusy(false);
  }

  function handleClose() {
    if (busy) return;
    reset();
    onClose();
  }

  async function handleSynthesize() {
    setBusy(true);
    setError(null);
    try {
      setDraft(
        await synthesizePersona({
          title: title.trim(),
          hint: hint.trim(),
          role: role || null,
        }),
      );
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Persona üretilemedi.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createPersona({
        title: draft.title,
        displayName: draft.displayName,
        role: draft.role,
        expertise: draft.expertise,
        gender: draft.gender,
        summary: draft.summary,
        charter: draft.charter,
        systemInstruction: draft.systemInstruction,
        tags: draft.tags,
        psychometrics: draft.psychometrics,
        themeColor: draft.themeColor,
        voiceId: draft.voiceId,
        avatarUrl: draft.avatarUrl,
        origin: draft.origin,
        // Boş seçim = alan hiç gönderilmez; backend katalog varsayılanını
        // uygular ve konsey ataması devreye girer.
        ...(modelId ? { modelId } : {}),
      });
      onCreated(created);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Persona kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  function patchDraft(patch: Partial<PersonaDraft>) {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  const canSynthesize = title.trim().length >= 3 && !busy;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={draft ? "Taslağı Gözden Geçir" : "Yeni Persona"}
      subtitle={
        draft
          ? "Model bir kimlik önerdi. Her alanı değiştirebilirsin — kaydetmeden önce mizacını da ayarla."
          : "Bir unvan yaz; model ondan ad, uzmanlık, görev tanımı, konuşma talimatı ve kişilik profili üretsin."
      }
      width="max-w-4xl"
      footer={
        draft ? (
          <>
            <Btn tone="ghost" onClick={() => setDraft(null)} disabled={busy}>
              Geri
            </Btn>
            <Btn tone="outline" onClick={handleSynthesize} loading={busy}>
              Yeniden Üret
            </Btn>
            <Btn tone="primary" onClick={handleSave} loading={busy}>
              Kütüphaneye Kaydet
            </Btn>
          </>
        ) : (
          <>
            <Btn tone="ghost" onClick={handleClose}>
              Vazgeç
            </Btn>
            <Btn tone="primary" onClick={handleSynthesize} disabled={!canSynthesize} loading={busy}>
              Personayı Üret
            </Btn>
          </>
        )
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {!draft ? (
          <>
            <Field
              label="Persona Başlığı"
              required
              hint="Gerçek dünyadaki mesleki unvan en iyi sonucu verir."
            >
              <TextInput
                value={title}
                autoFocus
                maxLength={120}
                placeholder="ör. Kıdemli Vergi Müfettişi"
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSynthesize) void handleSynthesize();
                }}
              />
            </Field>

            <div className="flex flex-wrap gap-1.5">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setTitle(ex)}
                  className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-slate-400 transition-colors hover:border-violet-500/40 hover:text-violet-200"
                >
                  {ex}
                </button>
              ))}
            </div>

            <Field
              label="Ek Yönlendirme"
              hint="Opsiyonel. Sektör, kurum tipi, mizaç veya odak noktası belirtebilirsin."
            >
              <TextArea
                rows={3}
                value={hint}
                maxLength={1000}
                placeholder="ör. Kamu kurumlarında 20 yıl çalışmış, prosedüre çok bağlı, sayısal kanıt olmadan görüş bildirmeyen biri."
                onChange={(e) => setHint(e.target.value)}
              />
            </Field>

            <Field
              label="Bilişsel Duruş"
              hint="Boş bırakırsan model unvana en uygun duruşu kendi seçer."
            >
              <Select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">Model seçsin</option>
                {(catalog?.roles ?? []).map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-3">
              <PersonaAvatar
                url={draft.avatarUrl}
                name={draft.displayName}
                color={draft.themeColor}
                size={52}
              />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label="Görünen Ad" required>
                    <TextInput
                      value={draft.displayName}
                      maxLength={60}
                      onChange={(e) => patchDraft({ displayName: e.target.value })}
                    />
                  </Field>
                  <Field label="Mesleki Unvan">
                    <TextInput
                      value={draft.expertise}
                      maxLength={80}
                      onChange={(e) => patchDraft({ expertise: e.target.value })}
                    />
                  </Field>
                </div>
                <Field label="Kütüphane Başlığı" hint="Listede aradığında göreceğin ad.">
                  <TextInput
                    value={draft.title}
                    maxLength={120}
                    onChange={(e) => patchDraft({ title: e.target.value })}
                  />
                </Field>
                <Field
                  label="Konuşma Modeli"
                  hint={
                    modelCatalog.status === "error"
                      ? "Model listesi alınamadı — varsayılanla kaydedilecek."
                      : "Boş bırakırsan konsey dağılımı karar verir."
                  }
                >
                  <Select
                    value={modelId}
                    disabled={modelCatalog.status !== "ready"}
                    onChange={(e) => setModelId(e.target.value)}
                  >
                    <option value="">
                      {modelCatalog.status === "loading"
                        ? "Yükleniyor…"
                        : "Konsey dağılımına bırak"}
                    </option>
                    {modelCatalog.models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </div>

            <Field label="Kısa Tanıtım">
              <TextArea
                rows={2}
                value={draft.summary}
                maxLength={400}
                onChange={(e) => patchDraft({ summary: e.target.value })}
              />
            </Field>

            <Field
              label="Değişmez Görev Tanımı"
              hint="Konu ne olursa olsun geçerli kalır — personanın anayasası. Her tartışmada system prompt'un en başına konur."
            >
              <TextArea
                rows={4}
                value={draft.charter}
                maxLength={900}
                onChange={(e) => patchDraft({ charter: e.target.value })}
              />
            </Field>

            <Field
              label="Konuşma Talimatı"
              hint="Nasıl konuşacağı, hangi kanıta güveneceği, nasıl itiraz edeceği."
            >
              <TextArea
                rows={7}
                value={draft.systemInstruction}
                maxLength={3000}
                onChange={(e) => patchDraft({ systemInstruction: e.target.value })}
              />
            </Field>

            {catalog && (
              <PsychometricEditor
                axes={catalog.axes}
                profile={draft.psychometrics}
                onChange={(next: PsychometricProfile) => patchDraft({ psychometrics: next })}
                {...(busy ? { disabled: true } : {})}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
